import { Button, Chip, LinkButton, Selector } from '@pmi/dsm-react';
import { CloseSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import { FilterIcon } from '@pmi/dsm-react/dist/components/icons/16';
import FilterAgreementStatusSelection from './FilterAgreementStatusSelection';
import styles from './filterBy.module.scss';
import FilterZoneSelection from './FilterZoneSelection';

export interface Option {
  label: string;
  value: string;
  disabled?: boolean;
  groupKey?: string;
}

export interface FilterUsersProps {
  loading: boolean;
  onReset: (reset: boolean) => void;
  onFilterChanged: (filters: FilterData) => void;
  selectedZones: string[];
  selectedAgreementStatuses: string[];
  visibleZoneValues?: string[];
  showFilterStatus?: boolean;
  includeActiveStatus?: boolean;
  includeDisabledStatus?: boolean;
  includeExpiringStatus?: boolean;
}

export interface FilterData {
  zones: string[];
  status: string[];
}

export interface FilterUserOption {
  label: string;
  value: string;
}

export default function FilterBy({
  loading,
  onReset,
  onFilterChanged,
  selectedZones,
  selectedAgreementStatuses,
  visibleZoneValues = undefined,
  showFilterStatus = true,
  includeActiveStatus = true,
  includeDisabledStatus = true,
  includeExpiringStatus = true,
}: FilterUsersProps) {
  const tempSelected = {
    selectedZones,
    selectedAgreementStatuses,
  };

  const agreementStatusList: Option[] = [];

  if (showFilterStatus) {
    if (includeActiveStatus) {
      agreementStatusList.push({
        label: 'Active',
        value: 'active',
      });
    }

    if (includeDisabledStatus) {
      agreementStatusList.push({
        label: 'Disabled',
        value: 'disabled',
      });
    }

    if (includeExpiringStatus) {
      agreementStatusList.push({
        label: 'Expiring Soon',
        value: 'expiring',
      });
    }
  }

  const allZoneList: Option[] = [
    {
      label: 'All Zones',
      value: 'allZones',
    },
    {
      label: 'Zone A',
      value: 'a',
    },
    {
      label: 'Zone B',
      value: 'b',
    },
    {
      label: 'Zone C',
      value: 'c',
    },
    {
      label: 'Brazil',
      value: 'br',
    },
    {
      label: 'India',
      value: 'in',
    },
    {
      label: 'United States',
      value: 'us',
    },
  ];

  const normalizedVisibleZoneValues = visibleZoneValues?.map((item) =>
    item.toLowerCase(),
  );

  const zoneList: Option[] = normalizedVisibleZoneValues
    ? allZoneList.filter(
        ({ value }) =>
          value === 'allZones' ||
          normalizedVisibleZoneValues.includes(value.toLowerCase()),
      )
    : allZoneList;

  const onApply = () => {
    if (
      tempSelected.selectedZones.length === 0 &&
      tempSelected.selectedAgreementStatuses.length === 0
    ) {
      onReset(true);
    }
    onFilterChanged({
      zones: tempSelected.selectedZones,
      status: tempSelected.selectedAgreementStatuses,
    });
    document.body.click();
  };
  const onResetClick = () => {
    tempSelected.selectedZones = [];
    tempSelected.selectedAgreementStatuses = [];
    const active = document.querySelectorAll(
      '.filter-user-selector-container ul li.active',
    );
    if (active && active.length > 0) {
      active.forEach((el) => {
        setTimeout(() => el.querySelector('label').click(), 1);
      });
    }
  };
  const removeZoneByPill = (zone: string) => {
    tempSelected.selectedZones = tempSelected.selectedZones.filter(
      (z) => z !== zone,
    );
    onApply();
  };

  const renderZonePill = (items: string[]) =>
    items.map((item, index) => {
      const zone = zoneList.find((z) => z.value === item);
      if (!zone) {
        return null;
      }
      return (
        <Chip
          chipTitle={zone.label}
          key={`zone-chip-${index.toString()}`}
          icon={
            <CloseSolidIcon
              fontSize="xs"
              onClick={() => removeZoneByPill(zone.value)}
              color="original"
            />
          }
          size="xs"
          className={`d-flex flex-row-reverse justify-content-end align-center align-content-center border-0 ${styles.chipSelected}`}
        />
      );
    });

  const removeStatusByPill = (satatus: string) => {
    tempSelected.selectedAgreementStatuses =
      tempSelected.selectedAgreementStatuses.filter((z) => z !== satatus);
    onApply();
  };
  const renderStatusPill = (items: string[]) =>
    items.map((item, index) => {
      const zone = agreementStatusList.find((z) => z.value === item);
      if (!zone) {
        return null;
      }
      return (
        <Chip
          chipTitle={zone.label}
          key={`zone-chip-${index.toString()}`}
          icon={
            <CloseSolidIcon
              fontSize="xs"
              onClick={() => removeStatusByPill(zone.value)}
              color="original"
            />
          }
          size="xs"
          className={`d-flex flex-row-reverse justify-content-end align-center align-content-center border-0 ${styles.chipSelected}`}
        />
      );
    });

  return (
    <div className="d-flex filter-user gap-4">
      <div className={`d-flex ${styles.filterSelection}`}>
        <Selector
          label="Filter"
          align="start"
          icon="filter"
          iconComponent={FilterIcon}
          withIconLabel
          fontSizeLabel="md"
          disabled={loading}
          variant="icon"
          className="filter-user-selector-container"
        >
          <div className={styles.filterSelectionContainer}>
            <FilterZoneSelection
              list={zoneList}
              selected={tempSelected.selectedZones}
              onChange={(selected: string[]) => {
                if (selected.includes('unrestricted')) {
                  tempSelected.selectedZones = ['unrestricted'];
                  return;
                }
                tempSelected.selectedZones = selected;
              }}
            />
            {showFilterStatus && (
              <FilterAgreementStatusSelection
                list={agreementStatusList}
                statuses={tempSelected.selectedAgreementStatuses}
                onChange={(selected: string[]) => {
                  tempSelected.selectedAgreementStatuses = selected;
                }}
              />
            )}
          </div>
          <div
            className={`${styles.filterActionButtons} ${
              showFilterStatus ? 'border-top' : ''
            } px-3 py-2 d-flex align-items-center gap-2`}
          >
            <div className="flex-grow-1">
              <LinkButton
                titleText="Clear All"
                className="clear-all"
                onClick={onResetClick}
                size="md"
              />
            </div>
            <div className="d-flex flex-grow-1 justify-content-end">
              <Button
                titleText="Apply"
                variant="primary"
                size="sm"
                onClick={onApply}
                className={styles.btnApply}
              />
            </div>
          </div>
        </Selector>
      </div>
      <div className="selected-filters d-flex flex-wrap gap-1 pt-2">
        {selectedZones.length > 0 && renderZonePill(selectedZones)}
        {selectedAgreementStatuses.length > 0 &&
          renderStatusPill(selectedAgreementStatuses)}
      </div>
    </div>
  );
}
